/**
 * Logging configuration interface for environment-specific settings
 * 
 * This interface defines all configurable options for the logging system
 * that can be customized per environment (development, staging, production).
 */
export interface LoggingConfig {
  /**
   * Maximum number of logs in a batch before transmission
   * @default 25
   */
  batchSize?: number;

  /**
   * Flush interval in milliseconds for automatic batch transmission
   * @default 5000 (5 seconds)
   */
  flushIntervalMs?: number;

  /**
   * Maximum retry attempts for failed log transmissions
   * @default 3
   */
  maxRetries?: number;

  /**
   * Maximum queue size before dropping oldest logs
   * @default 1000
   */
  maxQueueSize?: number;

  /**
   * Enable console output in browser
   * If not specified, defaults based on environment:
   * - development: true
   * - staging: true
   * - production: false
   */
  enableConsoleOutput?: boolean;

  /**
   * CloudWatch log group name
   * If not specified, defaults based on environment:
   * - development: 'ground-control-dev'
   * - staging: 'ground-control-test'
   * - production: 'ground-control-prod'
   */
  logGroup?: string;

  /**
   * Enable CloudWatch logging on application startup
   * @default true
   */
  enableCloudWatchOnStartup?: boolean;

  /**
   * Enable debug logging for the logging system itself
   * Useful for troubleshooting logging issues
   * @default false (true in development)
   */
  enableDebugLogging?: boolean;

  /**
   * Enable metrics reporting in console (development only)
   * @default true in development, false otherwise
   */
  enableMetricsReporting?: boolean;

  /**
   * Metrics reporting interval in milliseconds
   * @default 30000 (30 seconds)
   */
  metricsReportingIntervalMs?: number;

  /**
   * Request timeout for CloudWatch API calls in milliseconds
   * @default 10000 (10 seconds)
   */
  apiTimeoutMs?: number;

  /**
   * Sensitive field patterns for automatic redaction
   * @default ['password', 'token', 'secret', 'apiKey', 'connectionString']
   */
  sensitiveFieldPatterns?: string[];

  /**
   * Maximum size for the authentication failure queue
   * Logs are queued when authentication fails and sent when authentication is restored
   * @default 100
   */
  authFailureQueueSize?: number;

  /**
   * Interval in milliseconds for retrying authentication checks
   * Used to periodically check if authentication has been restored
   * @default 30000 (30 seconds)
   */
  authRetryIntervalMs?: number;

  /**
   * Suppress 401 authentication error modals during logout process
   * Prevents error dialogs from appearing when user is logging out
   * @default true
   */
  suppressAuthErrorsOnLogout?: boolean;
}

/**
 * Default logging configuration values
 */
export const DEFAULT_LOGGING_CONFIG: Required<LoggingConfig> = {
  batchSize: 25,
  flushIntervalMs: 5000,
  maxRetries: 3,
  maxQueueSize: 1000,
  enableConsoleOutput: true,
  logGroup: 'ground-control-dev',
  enableCloudWatchOnStartup: true,
  enableDebugLogging: false,
  enableMetricsReporting: false,
  metricsReportingIntervalMs: 30000,
  apiTimeoutMs: 10000,
  sensitiveFieldPatterns: [
    'password',
    'pwd',
    'passwd',
    'token',
    'accessToken',
    'refreshToken',
    'secret',
    'apiSecret',
    'clientSecret',
    'apiKey',
    'key',
    'connectionString',
    'connStr',
    'authorization',
    'auth'
  ],
  authFailureQueueSize: 100,
  authRetryIntervalMs: 30000,
  suppressAuthErrorsOnLogout: true
};

/**
 * Development environment logging configuration
 */
export const DEVELOPMENT_LOGGING_CONFIG: LoggingConfig = {
  enableConsoleOutput: true,
  logGroup: 'ground-control-dev',
  enableDebugLogging: true,
  enableMetricsReporting: true,
  enableCloudWatchOnStartup: true
};

/**
 * Staging/Test environment logging configuration
 */
export const STAGING_LOGGING_CONFIG: LoggingConfig = {
  enableConsoleOutput: true,
  logGroup: 'ground-control-test',
  enableDebugLogging: false,
  enableMetricsReporting: false,
  enableCloudWatchOnStartup: true
};

/**
 * Production environment logging configuration
 */
export const PRODUCTION_LOGGING_CONFIG: LoggingConfig = {
  enableConsoleOutput: false,
  logGroup: 'ground-control-prod',
  enableDebugLogging: false,
  enableMetricsReporting: false,
  enableCloudWatchOnStartup: true,
  // Optimize for production performance
  batchSize: 50, // Larger batches for efficiency
  flushIntervalMs: 10000, // Less frequent flushes (10 seconds)
  maxQueueSize: 500 // Smaller queue to reduce memory usage
};
